import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useMaterialNode } from '@/graph-store/hooks';
import { useGraphEditorStore } from '@/graph-store';
import type { OperationTypeDefinition, QuantityType, Unit } from '@/graph/domain/types';
import { SYMBOLIC_QUANTITY_VALUES } from '@/graph/domain/types';
import {
  getRecommendedInstrumentOptions,
  type RecommendedInstrumentOption,
} from '@services/runApi.service';
import type { RecommendedInstrumentRef } from './types';
import { StepHeader } from './StepHeader';
import { DurationField } from './DurationField';
import { SelectDestinationModal } from './SelectDestinationModal';
import { AddCheckpointDialog } from './AddCheckpointDialog';
import { CheckpointList } from './CheckpointList';
import type {
  TransferRowFormState,
  TransferFormState,
} from '@/graph-store/actions/transferHelpers';
import type { Checkpoint } from '@/graph/domain/types';
import {
  defaultUnitForType,
  computeOverflow,
} from '@/graph-store/actions/transferHelpers';
import { generateId } from '@/graph/utils/id';

const MAX_RECOMMENDED_INSTRUMENTS = 5;
const RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID = 'transfer-recommended-instrument-autocomplete';

// ── Types ───────────────────────────────────────────────────────────────────

interface TransferDetailsPanelProps {
  sourceMaterialId: string;
  batchMode?: boolean;
  excludedDestinationMaterialIds?: string[];
  operationType: OperationTypeDefinition;
  onSave: (form: TransferFormState) => void;
  onFormChange?: (form: TransferFormState) => void;
  invalidDestinationRowIds?: string[];
  destinationValidationRequestId?: number;
  /** Pre-populated state for edit mode */
  initialState?: Partial<TransferFormState>;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function createEmptyRow(quantityType: QuantityType): TransferRowFormState {
  return {
    id: generateId('trow'),
    mode: 'numeric',
    amount: null,
    unit: defaultUnitForType(quantityType),
    symbolic: null,
    destinationMaterialId: null,
    destinationDraft: null,
  };
}

const QUANTITY_TYPES: QuantityType[] = ['Volume', 'Weight', 'Count'];

const UNIT_OPTIONS: Record<QuantityType, Unit[]> = {
  Volume: ['nL', 'µL', 'mL', 'L'],
  Weight: ['µg', 'mg', 'g', 'kg'],
  Count: ['count'],
};

function getUnitLabel(unit: Unit): string {
  if (unit === 'g') return 'gm';
  if (unit === 'µg') return 'ug';
  return unit;
}

// ── Component ───────────────────────────────────────────────────────────────

export function TransferDetailsPanel({
  sourceMaterialId,
  batchMode = false,
  excludedDestinationMaterialIds = [],
  operationType,
  onSave,
  onFormChange,
  invalidDestinationRowIds = [],
  destinationValidationRequestId,
  initialState,
}: TransferDetailsPanelProps) {
  const source = useMaterialNode(sourceMaterialId);
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);

  const [quantityType, setQuantityType] = useState<QuantityType>(
    initialState?.quantityType ?? source?.quantity?.type ?? 'Volume',
  );
  const [sameVolume, setSameVolume] = useState(initialState?.sameVolume ?? false);
  const initialSourceHasQuantity =
    source?.quantity != null &&
    Number.isFinite(source.quantity.value?.amount) &&
    (source.quantity.value?.amount ?? 0) > 0;
  const initialRowAmount = initialState?.rows?.[0]?.amount;
  const hasZeroInitialRow = initialRowAmount === 0 || (initialRowAmount != null && initialRowAmount <= 0);

  const [transferEntireVolume, setTransferEntireVolume] = useState(
    !initialSourceHasQuantity || hasZeroInitialRow
      ? false
      : (initialState?.transferEntireVolume ?? false),
  );
  const [rows, setRows] = useState<TransferRowFormState[]>(() =>
    initialState?.rows?.length
      ? (batchMode ? [initialState.rows[0]] : initialState.rows)
      : [createEmptyRow(source?.quantity?.type ?? 'Volume')],
  );
  const [notes, setNotes] = useState(initialState?.notes ?? '');
  const [recommendedInstrument, setRecommendedInstrument] = useState<RecommendedInstrumentRef[]>(
    initialState?.recommendedInstrument ?? []
  );
  const [recommendedOptions, setRecommendedOptions] = useState<RecommendedInstrumentOption[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [recommendedOpen, setRecommendedOpen] = useState(false);
  const [durationHours, setDurationHours] = useState(initialState?.durationHours ?? 0);
  const [durationMinutes, setDurationMinutes] = useState(initialState?.durationMinutes ?? 0);
  const [durationSeconds, setDurationSeconds] = useState(initialState?.durationSeconds ?? 0);
  const [linkToPreviousStep, setLinkToPreviousStep] = useState(
    initialState?.linkToPreviousStep ?? false,
  );
  const [linkedStepId, setLinkedStepId] = useState(initialState?.linkedStepId ?? null);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>(
    initialState?.checkpoints ?? operationType.checkpoints ?? [],
  );

  // Dialog state for checkpoints
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Destination picker state
  const [pickerRowIndex, setPickerRowIndex] = useState<number | null>(null);
  const destinationButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const destinationRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const recommendedFieldRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadInstruments = async () => {
      setRecommendedLoading(true);
      try {
        const options = await getRecommendedInstrumentOptions();
        if (!cancelled) {
          setRecommendedOptions(options);
        }
      } catch {
        if (!cancelled) {
          setRecommendedOptions([]);
        }
      } finally {
        if (!cancelled) {
          setRecommendedLoading(false);
        }
      }
    };

    void loadInstruments();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!recommendedOpen) return;

    const handleScrollOutside = (event: Event) => {
      const root = recommendedFieldRef.current;
      const target = event.target as EventTarget | null;
      if (!root || !target) return;

      const targetNode = target as Node;
      if (root.contains(targetNode)) {
        return;
      }

      const targetElement = target as Element;
      const isInsideRecommendedDropdown =
        typeof targetElement?.closest === 'function' &&
        (
          Boolean(targetElement.closest('[data-recommended-instrument-listbox="true"]')) ||
          Boolean(targetElement.closest('[data-recommended-instrument-popper="true"]')) ||
          Boolean(targetElement.closest(`#${RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}-listbox`)) ||
          Boolean(targetElement.closest(`#${RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}-popup`))
        );

      if (!isInsideRecommendedDropdown) {
        setRecommendedOpen(false);
      }
    };

    document.addEventListener('scroll', handleScrollOutside, true);
    return () => {
      document.removeEventListener('scroll', handleScrollOutside, true);
    };
  }, [recommendedOpen]);

  // Recommended instrument helper functions
  const optionById = new Map(recommendedOptions.map((item) => [item.value, item]));
  const selectedRecommendedOptions = recommendedInstrument.map((saved) => {
    const match = optionById.get(saved.value);
    if (match) return match;
    return {
      _id: saved.value,
      value: saved.value,
      label: saved.value,
      name: saved.value,
      workspaceId: saved.workspaceId,
    } as RecommendedInstrumentOption;
  });

  const toInstrumentRefs = (items: RecommendedInstrumentOption[]): RecommendedInstrumentRef[] =>
    items.slice(0, MAX_RECOMMENDED_INSTRUMENTS).map((item) => ({
      value: item.value,
      workspaceId: item.workspaceId || operationType.workspaceId,
    }));

  const selectedRecommendedIds = new Set(selectedRecommendedOptions.map((item) => item.value));

  const getStatusBadgeSx = (status?: string) => {
    const normalized = String(status || '').toUpperCase().trim();

    if (normalized === 'AVAILABLE') {
      return { backgroundColor: '#d4edda', color: '#155724' };
    }
    if (normalized === 'OBSOLETE' || normalized.includes('INACTIVE') || normalized.includes('RETIRED')) {
      return { backgroundColor: '#f8d7da', color: '#721c24' };
    }
    if (normalized.includes('MAINTENANCE') || normalized.includes('INSTALLATION') || normalized === 'NOT FUNCTIONAL') {
      return { backgroundColor: '#fff3cd', color: '#856404' };
    }
    if (normalized.includes('CALIBRATION')) {
      return { backgroundColor: '#d1ecf1', color: '#0c5460' };
    }

    return { backgroundColor: '#e2e8f0', color: '#4a5568' };
  };

  const existingCheckpointNames = checkpoints.map((cp) => cp.checkpointName);

  if (!source) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Source material not found.</Typography>
      </Box>
    );
  }

  const sourceHasNumericQuantity =
    source.quantity != null &&
    Number.isFinite(source.quantity.value?.amount) &&
    (source.quantity.value?.amount ?? 0) > 0;
  const entireVolumeDisabled = !sourceHasNumericQuantity;

  useEffect(() => {
    if (entireVolumeDisabled && transferEntireVolume) {
      setTransferEntireVolume(false);
    }
  }, [entireVolumeDisabled, transferEntireVolume]);

  // Compute "Transfer Entire Volume" amounts
  const computedRows = useMemo(() => {
    if (!transferEntireVolume || entireVolumeDisabled || !source.quantity) return rows;
    const totalAmount = source.quantity.value.amount;
    const numericRows = rows.filter((r) => r.mode === 'numeric');
    const perRow =
      numericRows.length > 0 ? totalAmount / numericRows.length : totalAmount;

    return rows.map((r) => {
      if (r.mode !== 'numeric') return r;
      return {
        ...r,
        amount: perRow,
        unit: source.quantity!.value.units,
      };
    });
  }, [rows, transferEntireVolume, entireVolumeDisabled, source.quantity]);

  // Same Volume: propagate row 0's values to rows 1..N
  const displayRows = useMemo(() => {
    if (!sameVolume) return computedRows;
    const first = computedRows[0];
    if (!first || first.mode !== 'numeric') return computedRows;
    return computedRows.map((r, i) => {
      if (i === 0 || r.mode !== 'numeric') return r;
      return { ...r, amount: first.amount, unit: first.unit };
    });
  }, [computedRows, sameVolume]);

  // Overflow check
  const overflow = useMemo(
    () => computeOverflow(source, displayRows),
    [source, displayRows],
  );

  // Push current form state to parent for footer-button save
  useEffect(() => {
    onFormChange?.({
      quantityType,
      transferEntireVolume,
      sameVolume,
      rows: displayRows,
      notes,
      recommendedInstrument,
      durationHours,
      durationMinutes,
      durationSeconds,
      linkToPreviousStep,
      linkedStepId,
      checkpoints,
    });
  }, [
    quantityType,
    transferEntireVolume,
    sameVolume,
    displayRows,
    notes,
    recommendedInstrument,
    durationHours,
    durationMinutes,
    durationSeconds,
    linkToPreviousStep,
    linkedStepId,
    checkpoints,
    onFormChange,
  ]);

  // Already-chosen destination IDs (for exclusion in picker)
  const chosenDestinationIds = useMemo(
    () =>
      rows
        .map((r) => r.destinationMaterialId)
        .filter((id): id is string => id != null),
    [rows],
  );

  const effectiveExcludedDestinationIds = useMemo(
    () => [...chosenDestinationIds, ...excludedDestinationMaterialIds],
    [chosenDestinationIds, excludedDestinationMaterialIds],
  );

  const invalidDestinationSet = useMemo(
    () => new Set(invalidDestinationRowIds),
    [invalidDestinationRowIds],
  );

  useEffect(() => {
    if (!invalidDestinationRowIds.length) return;

    const firstInvalidId = invalidDestinationRowIds[0];
    const rowElement = destinationRowRefs.current[firstInvalidId];
    rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const button = destinationButtonRefs.current[firstInvalidId];
    button?.focus();
  }, [invalidDestinationRowIds, destinationValidationRequestId]);

  // ── Row handlers ──────────────────────────────────────────────────────────

  const updateRow = useCallback(
    (index: number, patch: Partial<TransferRowFormState>) => {
      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow(quantityType)]);
  }, [quantityType]);

  const removeRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Quantity type change resets units
  const handleQuantityTypeChange = useCallback(
    (newType: QuantityType) => {
      setQuantityType(newType);
      const newUnit = defaultUnitForType(newType);
      setRows((prev) =>
        prev.map((r) =>
          r.mode === 'numeric' ? { ...r, unit: newUnit } : r,
        ),
      );
    },
    [],
  );

  // Destination picker
  const handleOpenPicker = useCallback((index: number) => {
    setPickerRowIndex(index);
  }, []);

  const handleSelectDestination = useCallback(
    (materialId: string) => {
      if (pickerRowIndex != null) {
        updateRow(pickerRowIndex, {
          destinationMaterialId: materialId,
          destinationDraft: null,
        });
      }
      setPickerRowIndex(null);
    },
    [pickerRowIndex, updateRow],
  );

  const handleCreateNewDestination = useCallback(() => {
    // Mirror Angular: close Select Destination, then open Add Material modal.
    // On save, material is added to the graph and selected as this row's destination.
    const rowIndex = pickerRowIndex;
    setPickerRowIndex(null);

    useGraphEditorStore.getState().openModal({
      kind: 'add-material',
      payload: {
        onSaved: (materialId: string) => {
          if (rowIndex == null) return;
          updateRow(rowIndex, {
            destinationMaterialId: materialId,
            destinationDraft: null,
          });
        },
      },
    });
  }, [pickerRowIndex, updateRow]);

  // ── Save ──────────────────────────────────────────────────────────────────

  // Checkpoint handlers
  const handleAddCheckpoint = (cp: Checkpoint) => {
    const updated = editingIndex !== null
      ? checkpoints.map((c, i) => (i === editingIndex ? { ...cp, isCustom: c.isCustom ?? true } : c))
      : [...checkpoints, { ...cp, isCustom: true }];
    setCheckpoints(updated);
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const handleEditCheckpoint = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleDeleteCheckpoint = (index: number) => {
    setCheckpoints(checkpoints.filter((_, i) => i !== index));
  };

  const handleOpenAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleSave = useCallback(() => {
    onSave({
      quantityType,
      transferEntireVolume,
      sameVolume,
      rows: displayRows,
      notes,
      recommendedInstrument,
      durationHours,
      durationMinutes,
      durationSeconds,
      linkToPreviousStep,
      linkedStepId,
      checkpoints,
    });
  }, [
    quantityType,
    transferEntireVolume,
    sameVolume,
    displayRows,
    notes,
    recommendedInstrument,
    durationHours,
    durationMinutes,
    durationSeconds,
    linkToPreviousStep,
    linkedStepId,
    checkpoints,
    onSave,
  ]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{ p: 2, overflowY: 'auto', height: '100%' }}
      data-testid="transfer-details-panel"
    >
      <StepHeader operationName={operationType.operationName} source={source} />
      <Divider sx={{ my: 1.5 }} />

      {/* Quantity Type */}
      <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
        <InputLabel>Quantity Type</InputLabel>
        <Select
          value={quantityType}
          label="Quantity Type"
          onChange={(e) =>
            handleQuantityTypeChange(e.target.value as QuantityType)
          }
        >
          {QUANTITY_TYPES.map((qt) => (
            <MenuItem key={qt} value={qt}>
              {qt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Checkboxes */}
      <Tooltip
        title={
          entireVolumeDisabled
            ? 'Source has no volume to transfer'
            : ''
        }
      >
        <FormControlLabel
          disabled={entireVolumeDisabled}
          control={
            <Checkbox
              checked={transferEntireVolume && !entireVolumeDisabled}
              onChange={(_, checked) => setTransferEntireVolume(checked)}
              size="small"
            />
          }
          label="Transfer Entire Volume"
        />
      </Tooltip>

      <FormControlLabel
        control={
          <Checkbox
            checked={sameVolume}
            onChange={(_, checked) => setSameVolume(checked)}
            size="small"
          />
        }
        label="Same Volume"
      />

      <Divider sx={{ my: 1.5 }} />

      {/* Destination Rows */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#204c77' }}>
        {batchMode ? 'Destination' : 'Destination Rows'}
      </Typography>

      {displayRows.map((row, index) => {
        const isNumericInputDisabled =
          (sameVolume && index > 0 && row.mode === 'numeric') ||
          (transferEntireVolume && row.mode === 'numeric');

        const destName =
          row.destinationMaterialId && materialNodes[row.destinationMaterialId]
            ? materialNodes[row.destinationMaterialId].name
            : row.destinationDraft
              ? row.destinationDraft.name
              : null;

        return (
          <Box
            key={row.id}
            ref={(el: HTMLDivElement | null) => {
              destinationRowRefs.current[row.id] = el;
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              p: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {/* Mode toggle */}
            <ToggleButton
              size="small"
              value={row.mode}
              selected={row.mode === 'symbolic'}
              onChange={() =>
                updateRow(index, {
                  mode: row.mode === 'numeric' ? 'symbolic' : 'numeric',
                })
              }
              sx={{ minWidth: 36, px: 0.5 }}
            >
              {row.mode === 'numeric' ? '123' : '~'}
            </ToggleButton>

            {/* Quantity or Symbolic */}
            {row.mode === 'numeric' ? (
              <>
                <TextField
                  type="number"
                  size="small"
                  placeholder="100"
                  value={row.amount ?? ''}
                  disabled={isNumericInputDisabled}
                  onChange={(e) =>
                    updateRow(index, {
                      amount: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  sx={{ width: 90 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <Select
                  size="small"
                  value={row.unit ?? ''}
                  disabled={isNumericInputDisabled}
                  onChange={(e) =>
                    updateRow(index, { unit: e.target.value as Unit })
                  }
                  sx={{ width: 80 }}
                >
                  {UNIT_OPTIONS[quantityType].map((u) => (
                    <MenuItem key={u} value={u}>
                      {getUnitLabel(u)}
                    </MenuItem>
                  ))}
                </Select>
              </>
            ) : (
              <Select
                size="small"
                value={row.symbolic ?? ''}
                onChange={(e) =>
                  updateRow(index, { symbolic: e.target.value || null })
                }
                displayEmpty
                sx={{ width: 170 }}
              >
                <MenuItem value="">
                  <em>Select symbolic...</em>
                </MenuItem>
                {SYMBOLIC_QUANTITY_VALUES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            )}

            {/* Destination Picker */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.35 }}>
              {destName ? (
                <Chip
                  label={destName}
                  size="small"
                  onClick={() => handleOpenPicker(index)}
                  onDelete={() =>
                    updateRow(index, {
                      destinationMaterialId: null,
                      destinationDraft: null,
                    })
                  }
                  sx={{ width: '100%' }}
                />
              ) : (
                <Button
                  ref={(el: HTMLButtonElement | null) => {
                    destinationButtonRefs.current[row.id] = el;
                  }}
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenPicker(index)}
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    ...(invalidDestinationSet.has(row.id) && {
                      borderColor: 'error.main',
                      color: 'error.main',
                      borderWidth: 2,
                    }),
                  }}
                >
                  Pick Destination
                </Button>
              )}

              {invalidDestinationSet.has(row.id) && !destName && (
                <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                  Destination is required
                </Typography>
              )}
            </Box>

            {/* Remove row */}
            <IconButton
              size="small"
              disabled={displayRows.length <= 1}
              onClick={() => removeRow(index)}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        sx={{ mb: 1.5, textTransform: 'none' }}
      >
        Add Destination
      </Button>

      {/* Overflow warning */}
      {overflow?.hasOverflow && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Total transferred ({overflow.totalTransferred.toFixed(2)}) exceeds
          available ({overflow.available.toFixed(2)})
          {overflow.hasSymbolicRows && ' (plus symbolic transfers)'}
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* ── Checkpoints ───────────────────────────────────────────
           Mirrors StepDetailsPanel checkpoint UI */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Checkpoints</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add Checkpoint
          </Button>
        </Box>

        {checkpoints.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
            No checkpoints added yet. Click "Add Checkpoint" to add one.
          </Typography>
        ) : (
          <CheckpointList
            checkpoints={checkpoints}
            canEdit={operationType.canEditCheckpoints}
            onEdit={(index) => handleEditCheckpoint(index)}
            onDelete={handleDeleteCheckpoint}
            workspaceId={operationType.workspaceId}
          />
        )}
      </Box>

      <Box sx={{ mt: 1.25 }}>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Duration */}
      <DurationField
        hours={durationHours}
        minutes={durationMinutes}
        seconds={durationSeconds}
        linkToPreviousStep={linkToPreviousStep}
        linkedStepId={linkedStepId}
        durationAllow={operationType.durationAllow !== false}
        durationRequired={operationType.durationRequired === true}
        onChange={(patch) => {
          if ('durationHours' in patch)
            setDurationHours(patch.durationHours!);
          if ('durationMinutes' in patch)
            setDurationMinutes(patch.durationMinutes!);
          if ('durationSeconds' in patch)
            setDurationSeconds(patch.durationSeconds!);
          if ('linkToPreviousStep' in patch)
            setLinkToPreviousStep(patch.linkToPreviousStep!);
          if ('linkedStepId' in patch)
            setLinkedStepId(patch.linkedStepId!);
        }}
      />

      {/* Recommended Instrument */}
      <Box ref={recommendedFieldRef} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
          Recommended Instrument
        </Typography>
        <Autocomplete
          id={RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}
          multiple
          disableCloseOnSelect
          open={recommendedOpen}
          onOpen={() => setRecommendedOpen(true)}
          onClose={() => setRecommendedOpen(false)}
          slotProps={{
            listbox: { 'data-recommended-instrument-listbox': 'true' } as any,
            popper: { 'data-recommended-instrument-popper': 'true' } as any,
          }}
          options={recommendedOptions}
          value={selectedRecommendedOptions}
          loading={recommendedLoading}
          getOptionDisabled={(option) =>
            selectedRecommendedOptions.length >= MAX_RECOMMENDED_INSTRUMENTS && !selectedRecommendedIds.has(option.value)
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label || option.name || option.value}
          onChange={(_, next) => setRecommendedInstrument(toInstrumentRefs(next))}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#fff',
                      color: '#2d3748',
                      border: '1px solid #d0d9e8',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
                      p: 0,
                      minWidth: 265,
                      maxWidth: 265,
                    },
                  },
                }}
                title={
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#f0f4fa',
                        borderBottom: '1px solid #d0d9e8',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.775rem',
                          fontWeight: 600,
                          color: '#1a4671',
                          maxWidth: 158,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {option.name || option.value}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          ...getStatusBadgeSx(option.status),
                          fontSize: '0.64rem',
                          fontWeight: 600,
                          px: 0.9,
                          py: 0.2,
                          borderRadius: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {option.status || '-'}
                      </Box>
                    </Box>

                    <Box sx={{ px: 1.5, py: 1.1 }}>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>System ID</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.uid || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Custom ID</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.externalUid || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Type</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.instrumentType || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Version</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.versionNumber || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Storage Location</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.location || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {option.name || option.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[option.uid, option.externalUid, option.collection].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Tooltip>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select Recommended Instrument"
              helperText={`Select up to ${MAX_RECOMMENDED_INSTRUMENTS} instruments (${selectedRecommendedOptions.length}/${MAX_RECOMMENDED_INSTRUMENTS})`}
            />
          )}
        />
      </Box>

      {/* Destination Picker Modal */}
      {pickerRowIndex != null && (
        <SelectDestinationModal
          sourceMaterialId={sourceMaterialId}
          excludedMaterialIds={effectiveExcludedDestinationIds}
          onSelectExisting={handleSelectDestination}
          onCreateNew={handleCreateNewDestination}
          onClose={() => setPickerRowIndex(null)}
        />
      )}

      {/* Checkpoint Dialog */}
      <AddCheckpointDialog
        open={dialogOpen}
        checkpointToEdit={editingIndex !== null ? checkpoints[editingIndex] : null}
        existingNames={existingCheckpointNames}
        operationTypeId={operationType.id}
        workspaceId={operationType.workspaceId}
        onConfirm={handleAddCheckpoint}
        onClose={() => { setDialogOpen(false); setEditingIndex(null); }}
      />
    </Box>
  );
}
